<template>
    <div class="relative -ml-3">
        <div
            v-if="showRefreshButtonAboveForm && displayIframe"
            class="flex justify-end"
        >
            <prime-button
                text
                class="ml-3 !p-0"
                :label="t('payments.refreshCardForm')"
                :disabled="isProcessing"
                @click="handleRefreshIframe"
            />
        </div>
        <iframe
            ref="hostedIframeWindow"
            :src="paymentSourceData.url"
            class="w-full"
            :class="displayIframe ? '' : 'invisible'"
            height="280"
            @load="iframeLoaded"
        />
        <div
            v-if="!displayIframe"
            class="absolute inset-0 top-4"
        >
            <prime-skeleton />
        </div>
        <div
            v-if="!showRefreshButtonAboveForm"
            class="flex justify-end"
        >
            <prime-button
                text
                class="ml-3 !p-0"
                :label="t('payments.refreshCardForm')"
                tabindex="-1"
                :disabled="isProcessing"
                @click="handleRefreshIframe"
            />
        </div>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const HostedPaymentFormPaymentSourceFragment = graphql(/* GraphQL */ `
    fragment HostedPaymentFormPaymentSourceFragment on PaymentSourceCreditCardCardpointe {
        url
    }
`)
</script>

<script setup lang="ts">
import PrimeSkeleton from 'primevue/skeleton'
import PrimeButton from 'primevue/button'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import { type TokenPaymentMethod } from '~/components/Checkout/Sections/Payment/payment-method'
import { useTimeoutFn } from '@vueuse/core'

const props = defineProps<{
    paymentSource: FragmentType<typeof HostedPaymentFormPaymentSourceFragment>
    refreshButtonAboveForm?: boolean
    isProcessing?: boolean
}>()

const emit = defineEmits<{
    (e: 'paymentMethod', payload: TokenPaymentMethod | undefined): void
}>()

const { t } = useI18n({ useScope: 'global' })

const paymentSourceData = computed(() =>
    getFragmentData(HostedPaymentFormPaymentSourceFragment, props.paymentSource)
)

const showRefreshButtonAboveForm = computed(() => props.refreshButtonAboveForm)

const displayIframe = ref(false)
function iframeLoaded(event: Event) {
    // Give the iframe time to load the styles
    useTimeoutFn(() => {
        displayIframe.value = true
    }, 200)
}
function resetIframe() {
    useTimeoutFn(() => {
        if (displayIframe.value === false) {
            emit('paymentMethod', undefined)
            hostedIframeWindow.value.src = paymentSourceData.value.url
            resetIframe()
        }
    }, 4000)
}

function handleRefreshIframe() {
    emit('paymentMethod', undefined)
    displayIframe.value = false
    hostedIframeWindow.value.src = paymentSourceData.value.url
    resetIframe()
}

onMounted(() => {
    resetIframe()
})

const hostedIframeWindow = ref<HTMLIFrameElement>()

const PaymentTokenExtendedTypeCreditCard = 'CREDIT-CARD'
const PaymentTokenCardPointePrefix = 'CP'
function tokenEventListener(event: MessageEvent) {
    // filter on iframe messages
    if (event.source != hostedIframeWindow.value?.contentWindow) {
        return
    }

    //
    // each provider has its own compact payment token format
    //

    // extract token and expiry
    const parsedMessage = JSON.parse(event.data)

    // invalid card data, so emit empty token
    if (parsedMessage.message.length == 0) {
        emit('paymentMethod', undefined)
        return
    }

    // CP is payment token prefix
    const token =
        PaymentTokenCardPointePrefix +
        '|' +
        parsedMessage.message +
        '|' +
        parsedMessage.expiry +
        '|' +
        PaymentTokenExtendedTypeCreditCard

    // Turn date into iso format - use 01 for day because it doesn't matter
    // for display
    const year = parsedMessage.expiry.substring(0, 4)
    let month = parsedMessage.expiry.substring(4)
    if (month.length === 1) {
        month = '0' + month
    }
    const accountExpirationDate = year + '-' + month + '-01'

    // Get the brand
    let brand = 'UNKNOWN'
    const brandDigits = parsedMessage.message.substring(1, 2)
    if (brandDigits === '3') {
        brand = 'AMEX'
    } else if (brandDigits === '4') {
        brand = 'VISA'
    } else if (brandDigits === '5') {
        brand = 'MASTERCARD'
    } else if (brandDigits === '6') {
        brand = 'DISCOVER'
    }

    const paymentMethod: TokenPaymentMethod = {
        type: 'TokenPaymentMethod',
        token,
        isCard: true,
        paymentTodayAllowed: true,
        brand,
        lastFour: parsedMessage.message.substring(
            parsedMessage.message.length - 4
        ),
        accountExpirationDate
    }

    emit('paymentMethod', paymentMethod)
}

onMounted(() => {
    window.addEventListener('message', tokenEventListener, false)
})

onUnmounted(() => {
    window.removeEventListener('message', tokenEventListener, false)
})
</script>
